import { useMemo, useState, type ReactNode } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useSession } from "@pontimed/auth-client";
import { cn } from "@pontimed/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeftRight, Clock, Crown, Loader2, Minus, Package, Trash2, Users } from "lucide-react";

import {
  CardActionDeclined,
  cardActionPublishableKey,
  confirmCardAction,
  requiredCardAction,
} from "./confirm-card-action";
import { BuyPackRow, SeatPickerSheet, type PackAddon, type SeatChoice } from "./pack-controls";
import { SubscriptionSeats } from "./subscription-seats";
import { Badge } from "@/components/ui/badge";
import { formatPrice } from "@/components/billing/format-price";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useProtectedAction, withProtectedSavingToast } from "@/lib/hooks/use-protected-action";
import {
  isPauseCarryError,
  isServicesPausedError,
  usePauseCarryBillingMessage,
  useServicesPausedBillingMessage,
} from "./services-paused-billing";

/**
 * Expanded subscription card — 1:1 port of legacy
 * `src/components/billing/subscription-expanded-card.tsx` (single source of
 * truth for one subscription's lifecycle UI per the
 * billing-subscription-card-redesign plan).
 *
 * Shows:
 *   - Plan summary header (name, price, renewal, change/cancel actions)
 *   - FREE INVENTORY: free seats (allocation via SubscriptionSeats) + free
 *     packs (unassigned, assignable to a seat) + the buy-a-pack row
 *   - Per active seat: assignee info + attached packs with move / return to
 *     inventory / stop renewal
 *
 * Fetches `/api/account/subscriptions/[id]/inventory` to get everything in
 * one call, and `/api/billing/plan-addons` for the packs sellable on this
 * plan. Mutations invalidate the inventory query so state reflects
 * server-side changes (some immediate, some deferred to the next renewal).
 *
 * Adaptations vs legacy (per recon):
 *   - `AllocatePanel` / `FreeSeatsBlock` → replaced by the sibling
 *     `SubscriptionSeats` component (it owns the allocate flow + its own
 *     Lingui strings). Rendered when free seats exist.
 *   - the seat dropdown + buy-confirmation modal → `<ResponsiveSheet>` via
 *     `./pack-controls` (mandated primitive; bottom sheet on mobile).
 *   - `availableAddons` prop dropped — the card always fetches the plan's
 *     packs itself, so callers stay unchanged.
 *   - every mutation reports through the standard saving → saved/error toast.
 */

// ─── Types ──────────────────────────────────────────────────────────────

interface Pack {
  id: string;
  featureKey: string;
  labelRo: string;
  labelEn: string;
  units: number;
  totalCents: number;
  currency: string;
  targetOwnerId: string | null;
  scheduledTargetOwnerId: string | null;
  scheduledAt: string | null;
  status: string;
}

interface Seat {
  id: string;
  allocationType: string;
  providerId: string | null;
  facilityId: string | null;
  businessId: string | null;
  targetEntityId: string | null;
  status: string;
  assigneeLabel: string;
  assigneeAvatar: string | null;
  invitedEmail: string | null;
  acceptedAt: string | null;
  invitedAt: string | null;
}

interface InventoryPayload {
  subscription: {
    id: string;
    planTier: string;
    planInterval: string;
    status: string;
    quantity: number;
    entityType: string | null;
    entityId: string | null;
    price: number;
    currency: string;
    currentPeriodEnd: string | null;
    plan: {
      code: string;
      name: Record<string, string>;
      priceMonthly: number;
      currency: string;
    } | null;
  };
  seats: Seat[];
  seatsTotal: number;
  seatsUsed: number;
  seatsPending: number;
  freePacks: Pack[];
  assignedPacks: Pack[];
}

interface BatchPurchaseResponse {
  ok: boolean;
  eventId: string;
  units: number;
  totalCents: number;
  /** 'pending' | 'posted' | 'failed' — 'failed' means Stripe rejected the item. */
  status: string;
}

/**
 * One pack mutation. `attach` is immediate; `reassign` / `detach` take effect
 * at the next renewal (the webhook applies them to the new-period pack).
 */
type PackAction =
  | { packId: string; kind: "attach"; targetOwnerId: string }
  | { packId: string; kind: "reassign"; targetOwnerId: string }
  | { packId: string; kind: "detach" }
  | { packId: string; kind: "cancelSchedule" }
  | { packId: string; kind: "stopRenewal" };

interface Props {
  subscriptionId: string;
  onChangePlan: () => void;
  onCancelSubscription: () => void;
  /**
   * Who pays this row — "· paid by you" next to the plan name (Wave 2b).
   * The provider workspace renders one card per plan row, and a doctor can be
   * covered by a clinic at the same time, so the payer has to be on the card.
   * Omitted everywhere else → header unchanged.
   */
  payerNote?: ReactNode;
  /**
   * Replaces the "price / interval · Renewal date" line. A founding row is free
   * for a fixed term, so "0 EUR / month" reads like a billed plan; the caller
   * passes "Free until <date>" instead.
   */
  headerNote?: ReactNode;
}

const BATCH_PURCHASE_PATH = "/api/features/overage/batch-purchase";

/**
 * One pack mutation → one API call.
 *   attach          PATCH  { targetOwnerId }          immediate
 *   reassign        PATCH  { scheduledTargetOwnerId } next renewal
 *   detach          PATCH  { scheduledTargetOwnerId: null } next renewal
 *   cancelSchedule  PATCH  { cancelSchedule: true }
 *   stopRenewal     DELETE                            keeps the current period
 */
function runPackAction(action: PackAction): Promise<unknown> {
  const path = `${BATCH_PURCHASE_PATH}/${action.packId}`;
  if (action.kind === "attach") {
    return apiFetch.patch(path, { targetOwnerId: action.targetOwnerId });
  }
  if (action.kind === "reassign") {
    return apiFetch.patch(path, { scheduledTargetOwnerId: action.targetOwnerId });
  }
  if (action.kind === "detach") {
    return apiFetch.patch(path, { scheduledTargetOwnerId: null });
  }
  if (action.kind === "cancelSchedule") {
    return apiFetch.patch(path, { cancelSchedule: true });
  }
  return apiFetch.delete(path);
}

// ─── Component ──────────────────────────────────────────────────────────

export function SubscriptionExpandedCard({
  subscriptionId,
  onChangePlan,
  onCancelSubscription,
  payerNote,
  headerNote,
}: Props) {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const queryClient = useQueryClient();
  const { user } = useSession();
  const sessionUser = user as { userId?: string; providerId?: string } | null;
  const hasProviderId = !!sessionUser?.providerId;
  const userId = sessionUser?.userId;
  // Buying a pack, stopping its renewal and revoking a seat are billing: the
  // API answers vault_locked while a PIN guarding "billing" is not unlocked
  // (buying a pack charges the card on file, so a PIN guarding "wallet" too) —
  // the gate opens and the call runs once more after the unlock.
  const { runProtected, PinGateElement } = useProtectedAction("billing");

  const inventoryPath = `/api/account/subscriptions/${subscriptionId}/inventory`;
  const inventoryKey = ["api", inventoryPath] as const;

  const query = useQuery({
    queryKey: inventoryKey,
    queryFn: () => apiFetch.get<InventoryPayload>(inventoryPath),
  });
  const data = query.data ?? null;

  // ── Packs sellable on this plan (buy-pack row) ────────────────────────
  const planCode = data?.subscription.plan?.code ?? null;
  // `subscriptionId`: priced as this subscription's top-up charges it — its
  // stored PPP and, on an annual plan, the year (the confirm says "per billing
  // period").
  const addonsPath = planCode
    ? `/api/billing/plan-addons?planCode=${encodeURIComponent(planCode)}&subscriptionId=${encodeURIComponent(subscriptionId)}`
    : null;
  const addonsQuery = useQuery({
    queryKey: ["api", addonsPath],
    queryFn: () => apiFetch.get<{ options: PackAddon[] }>(addonsPath as string),
    enabled: !!addonsPath,
    staleTime: 5 * 60_000,
  });
  const addons = useMemo(() => addonsQuery.data?.options ?? [], [addonsQuery.data]);

  // ── Mutations ─────────────────────────────────────────────────────────
  const invalidateInventory = () => queryClient.invalidateQueries({ queryKey: inventoryKey });

  const servicesPausedMessage = useServicesPausedBillingMessage();
  const pauseCarryMessage = usePauseCarryBillingMessage();
  const buyPack = useMutation({
    mutationFn: (addon: PackAddon) =>
      withProtectedSavingToast(
        async () => {
          const buy = (pendingEventId?: string) =>
            runProtected(() =>
              apiFetch.post<BatchPurchaseResponse>(BATCH_PURCHASE_PATH, {
                featureKey: addon.featureKey,
                subscriptionId,
                ...(pendingEventId ? { pendingEventId } : {}),
              }),
            );
          let res: BatchPurchaseResponse;
          try {
            res = await buy();
          } catch (err) {
            // The pack is paid at once; when the bank asks for 3-D Secure the
            // customer confirms it here, then the purchase is sent again with
            // its pending id — recorded, not bought twice.
            const clientSecret = requiredCardAction(err);
            const pendingEventId =
              err instanceof ApiError ? (err.body as { eventId?: string } | null)?.eventId : null;
            if (!clientSecret || !pendingEventId) throw err;
            if (!(await confirmCardAction(clientSecret, cardActionPublishableKey(err)))) {
              // Not confirmed: drop the held pack (its invoice is voided), so
              // a later retry of that invoice can never buy it unseen.
              await runProtected(() =>
                apiFetch.delete(`${BATCH_PURCHASE_PATH}/${pendingEventId}`),
              ).catch(() => undefined);
              throw new CardActionDeclined();
            }
            res = await buy(pendingEventId);
          }
          // The API answers 200 with status='failed' when Stripe refused the
          // subscription item — surface that as an error, not a silent success.
          if (res.status === "failed") throw new Error("pack_purchase_failed");
          return res;
        },
        {
          saving: t`Buying pack…`,
          saved: t`Pack added`,
          error: (err) =>
            isServicesPausedError(err)
              ? servicesPausedMessage
              : isPauseCarryError(err)
                ? pauseCarryMessage(err)
                : t`Could not buy the pack`,
        },
      ),
    onSuccess: invalidateInventory,
  });
  const busyAddonKey = buyPack.isPending ? (buyPack.variables?.featureKey ?? null) : null;

  const packActionMessages: Record<
    PackAction["kind"],
    { saving: string; saved: string; error: string }
  > = {
    attach: {
      saving: t`Assigning pack…`,
      saved: t`Pack assigned`,
      error: t`Could not assign the pack`,
    },
    reassign: {
      saving: t`Scheduling the move…`,
      saved: t`Move scheduled for the next renewal`,
      error: t`Could not schedule the move`,
    },
    detach: {
      saving: t`Scheduling the return…`,
      saved: t`Pack returns to inventory at the next renewal`,
      error: t`Could not schedule the change`,
    },
    cancelSchedule: {
      saving: t`Cancelling the scheduled change…`,
      saved: t`Scheduled change cancelled`,
      error: t`Could not cancel the scheduled change`,
    },
    stopRenewal: {
      saving: t`Stopping renewal…`,
      saved: t`Renewal stopped — the pack stays active until the period ends`,
      error: t`Could not stop the renewal`,
    },
  };

  const packAction = useMutation({
    mutationFn: (action: PackAction) =>
      withProtectedSavingToast(
        () => runProtected(() => runPackAction(action)),
        packActionMessages[action.kind],
      ),
    onSuccess: invalidateInventory,
  });

  // ── Seat revoke ───────────────────────────────────────────────────────
  const revokeSeat = useMutation({
    mutationFn: (allocationId: string) =>
      withProtectedSavingToast(
        () =>
          runProtected(() =>
            apiFetch.patch("/api/account/seats", { allocationId, action: "revoke" }),
          ),
        {
          saving: t`Revoking seat…`,
          saved: t`Seat revoked`,
          error: t`Could not revoke the seat`,
        },
      ),
    onSuccess: invalidateInventory,
  });
  const busyRevokeId = revokeSeat.isPending ? revokeSeat.variables : null;
  const busyPackId = packAction.isPending ? packAction.variables.packId : null;

  // ── Seat picker (assign a free pack / move an attached one) ───────────
  const [seatPicker, setSeatPicker] = useState<{
    mode: "assign" | "move";
    packId: string;
    excludeTargetId?: string;
  } | null>(null);

  const activeSeats: SeatChoice[] = useMemo(
    () =>
      (data?.seats ?? [])
        .filter((s): s is Seat & { targetEntityId: string } =>
          Boolean(s.status === "active" && s.targetEntityId),
        )
        .map((s) => ({
          id: s.id,
          targetEntityId: s.targetEntityId,
          assigneeLabel: s.assigneeLabel,
        })),
    [data],
  );
  const pickerSeats = seatPicker
    ? activeSeats.filter((s) => s.targetEntityId !== seatPicker.excludeTargetId)
    : [];

  // ── Pack label per locale (legacy: labelEn for en*, labelRo otherwise) ─
  const label = (p: { labelRo: string; labelEn: string }) =>
    locale.startsWith("en") ? p.labelEn : p.labelRo;

  // The inventory endpoint still echoes the raw featureKey as labelRo/labelEn
  // (TODO(W7) in apps/api). Prefer the catalog label from /api/billing/plan-addons
  // whenever the same feature is sold as a pack on this plan.
  const addonByKey = useMemo(() => new Map(addons.map((a) => [a.featureKey, a])), [addons]);
  const packLabel = (p: Pack) => label(addonByKey.get(p.featureKey) ?? p);

  // ── Derived state ─────────────────────────────────────────────────────
  const packsBySeat = useMemo(() => {
    const map = new Map<string, Pack[]>();
    if (!data) return map;
    for (const p of data.assignedPacks) {
      if (!p.targetOwnerId) continue;
      const list = map.get(p.targetOwnerId) ?? [];
      list.push(p);
      map.set(p.targetOwnerId, list);
    }
    return map;
  }, [data]);

  // ── Render ────────────────────────────────────────────────────────────
  if (query.isLoading) {
    // Skeleton mirrors the real layout: plan header, then the inventory block.
    return (
      <div
        className="bg-card border-border overflow-hidden rounded-xl border"
        aria-busy="true"
        aria-label={t`Loading subscription`}
      >
        <div className="bg-muted/30 border-border flex items-center gap-3 border-b p-5">
          <Skeleton className="size-10 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-56" />
          </div>
        </div>
        <div className="space-y-3 p-5">
          <Skeleton className="h-3 w-28" />
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-11 w-44 rounded-lg sm:h-8" />
        </div>
      </div>
    );
  }
  if (query.isError || !data) {
    return (
      <div className="bg-card border-border overflow-hidden rounded-xl border">
        <div className="text-destructive p-6 text-sm">
          {query.error instanceof Error ? query.error.message : <Trans>Unknown error</Trans>}
        </div>
      </div>
    );
  }

  // D2: `planTier` is the code suffix ("expert"), not the plan name ("Premium").
  // `pricing_plans.name` is the label; the tier word is only a last-resort
  // fallback for a deleted plan, and is capitalised so it never reads like a
  // machine value.
  const planName =
    data.subscription.plan?.name?.[locale] ??
    data.subscription.plan?.name?.en ??
    data.subscription.plan?.name?.ro ??
    (data.subscription.planTier
      ? data.subscription.planTier.charAt(0).toUpperCase() + data.subscription.planTier.slice(1)
      : "");
  const quantity = data.subscription.quantity;
  const seatsFree = Math.max(0, data.seatsTotal - data.seatsUsed - data.seatsPending);
  const renewalDate = data.subscription.currentPeriodEnd
    ? new Date(data.subscription.currentPeriodEnd).toLocaleDateString(locale, {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : null;

  return (
    <div className="bg-card border-border overflow-hidden rounded-xl border">
      {/* Plan summary header */}
      <div className="bg-muted/30 border-border flex flex-wrap items-center justify-between gap-3 border-b p-5">
        <div className="flex items-center gap-3">
          <div className="bg-primary/10 text-primary flex size-10 items-center justify-center rounded-lg">
            <Crown className="size-5" />
          </div>
          <div>
            <div className="text-foreground text-base font-semibold">
              {planName}
              {payerNote && (
                <span className="text-muted-foreground ml-1 text-sm font-normal">{payerNote}</span>
              )}
              {quantity > 1 && (
                <span className="text-muted-foreground ml-2 text-sm font-normal">
                  <Trans>× {quantity} seats</Trans>
                </span>
              )}
            </div>
            <div className="text-muted-foreground text-xs tabular-nums">
              {headerNote ?? (
                <>
                  {formatPrice(data.subscription.price, data.subscription.currency, locale)} /{" "}
                  {data.subscription.planInterval === "annual" ? t`year` : t`month`}
                  {renewalDate && (
                    <>
                      {" "}
                      · {t`Renewal`} {renewalDate}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="touch" variant="outline" onClick={onChangePlan}>
            <Trans>Change plan</Trans>
          </Button>
          <Button size="touch" variant="ghost" onClick={onCancelSubscription}>
            <Trans>Cancel</Trans>
          </Button>
        </div>
      </div>

      {/* FREE INVENTORY */}
      <div className="space-y-3 p-5">
        <SectionHeader
          icon={<Package className="size-4" />}
          title={<Trans>Free inventory</Trans>}
        />

        {/* Free seats — SubscriptionSeats owns the allocate flow (legacy
            FreeSeatsBlock + AllocatePanel equivalent). It refetches its own
            seat data; the inventory query refreshes on window focus. One
            doctor's own plan (provider, 1 licence) already covers him: no
            seat block there. */}
        {seatsFree > 0 && !(data.subscription.entityType === "provider" && quantity === 1) && (
          <SubscriptionSeats
            sub={{
              id: data.subscription.id,
              planTier: data.subscription.planTier,
              status: data.subscription.status,
              quantity: data.subscription.quantity,
              plan: data.subscription.plan
                ? {
                    code: data.subscription.plan.code,
                    name: data.subscription.plan.name,
                    // Inventory payload carries no feature map — empty is
                    // safe (SubscriptionSeats only needs code + name here).
                    features: {},
                  }
                : null,
            }}
            hasProviderId={hasProviderId}
            userId={userId}
          />
        )}

        {/* Free packs — unassigned capacity: assign to an active seat
            (immediate) or stop its renewal. */}
        {data.freePacks.length > 0 && (
          <div className="space-y-2">
            {data.freePacks.map((p) => (
              <PackFreeRow
                key={p.id}
                pack={p}
                label={packLabel(p)}
                hasActiveSeats={activeSeats.length > 0}
                busy={busyPackId === p.id}
                onAssign={() => setSeatPicker({ mode: "assign", packId: p.id })}
                onStopRenewal={() => packAction.mutate({ packId: p.id, kind: "stopRenewal" })}
              />
            ))}
          </div>
        )}

        {seatsFree === 0 && data.freePacks.length === 0 && (
          <div className="text-muted-foreground text-xs italic">
            <Trans>No free seats or unassigned packs.</Trans>
          </div>
        )}

        {/* Buy a pack — options come from the plan's batch-configured features */}
        {addonsQuery.isLoading ? (
          <div className="flex gap-2 pt-1">
            <div className="bg-muted h-11 w-44 animate-pulse rounded-lg sm:h-8" />
            <div className="bg-muted h-11 w-36 animate-pulse rounded-lg sm:h-8" />
          </div>
        ) : (
          <BuyPackRow
            addons={addons}
            label={label}
            busyFeatureKey={busyAddonKey}
            onBuy={(addon) => buyPack.mutate(addon)}
          />
        )}
      </div>

      {/* ALLOCATED SEATS */}
      {data.seats.length > 0 && (
        <div className="border-border space-y-3 border-t p-5">
          <SectionHeader
            icon={<Users className="size-4" />}
            title={<Trans>Allocated seats</Trans>}
          />
          <div className="space-y-3">
            {data.seats.map((seat) => (
              <SeatBlock
                key={seat.id}
                seat={seat}
                packs={seat.targetEntityId ? (packsBySeat.get(seat.targetEntityId) ?? []) : []}
                canMovePacks={activeSeats.length > 1}
                busyPackId={busyPackId}
                isRevoking={busyRevokeId === seat.id}
                onRevokeSeat={() => revokeSeat.mutate(seat.id)}
                onMovePack={(packId) =>
                  setSeatPicker({
                    mode: "move",
                    packId,
                    excludeTargetId: seat.targetEntityId ?? undefined,
                  })
                }
                onDetachPack={(packId) => packAction.mutate({ packId, kind: "detach" })}
                onCancelSchedule={(packId) => packAction.mutate({ packId, kind: "cancelSchedule" })}
                onStopRenewal={(packId) => packAction.mutate({ packId, kind: "stopRenewal" })}
                labelPack={packLabel}
              />
            ))}
          </div>
        </div>
      )}

      <SeatPickerSheet
        open={!!seatPicker}
        title={seatPicker?.mode === "move" ? <Trans>Move pack</Trans> : <Trans>Assign pack</Trans>}
        description={
          seatPicker?.mode === "move" ? (
            <Trans>The move takes effect at the next renewal.</Trans>
          ) : (
            <Trans>The pack becomes available on that seat right away.</Trans>
          )
        }
        seats={pickerSeats}
        busy={packAction.isPending}
        onPick={(targetEntityId) => {
          if (!seatPicker) return;
          packAction.mutate(
            seatPicker.mode === "assign"
              ? { packId: seatPicker.packId, kind: "attach", targetOwnerId: targetEntityId }
              : { packId: seatPicker.packId, kind: "reassign", targetOwnerId: targetEntityId },
          );
          setSeatPicker(null);
        }}
        onClose={() => setSeatPicker(null)}
      />
      {PinGateElement}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────

function SectionHeader({ icon, title }: { icon: ReactNode; title: ReactNode }) {
  return (
    <div className="text-foreground flex items-center gap-2 text-xs font-semibold tracking-wider uppercase">
      {icon}
      <span>{title}</span>
    </div>
  );
}

function PackFreeRow({
  pack,
  label,
  hasActiveSeats,
  busy,
  onAssign,
  onStopRenewal,
}: {
  pack: Pack;
  label: string;
  hasActiveSeats: boolean;
  busy: boolean;
  onAssign: () => void;
  onStopRenewal: () => void;
}) {
  const { t, i18n } = useLingui();
  const stopRenewalLabel = t`Stop renewal`;

  return (
    <div className="bg-background rounded-lg border p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-foreground text-sm font-medium">
            +{pack.units} {label}
          </div>
          <div className="text-muted-foreground text-xs">
            {formatPrice(pack.totalCents / 100, pack.currency, i18n.locale)}
            <span className="ml-1">
              · <Trans>unassigned</Trans>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {hasActiveSeats ? (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={onAssign}
              className="h-11 gap-1 px-3 sm:h-8"
            >
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : null}
              <Trans>Assign</Trans>
            </Button>
          ) : (
            <span className="text-muted-foreground text-[11px] italic">
              <Trans>No active seats</Trans>
            </span>
          )}
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={onStopRenewal}
            className="size-11 p-0 sm:size-8"
            title={stopRenewalLabel}
            aria-label={stopRenewalLabel}
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function SeatBlock({
  seat,
  packs,
  canMovePacks,
  busyPackId,
  isRevoking,
  onRevokeSeat,
  onMovePack,
  onDetachPack,
  onCancelSchedule,
  onStopRenewal,
  labelPack,
}: {
  seat: Seat;
  packs: Pack[];
  canMovePacks: boolean;
  busyPackId: string | null;
  isRevoking: boolean;
  onRevokeSeat: () => void;
  onMovePack: (packId: string) => void;
  onDetachPack: (packId: string) => void;
  onCancelSchedule: (packId: string) => void;
  onStopRenewal: (packId: string) => void;
  labelPack: (p: Pack) => string;
}) {
  const { t } = useLingui();
  const revokeTitle = seat.status === "pending_accept" ? t`Cancel invitation` : t`Revoke seat`;

  return (
    <div className="bg-background rounded-lg border p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {seat.assigneeAvatar ? (
            <img src={seat.assigneeAvatar} alt="" className="size-8 rounded-full object-cover" />
          ) : (
            <div className="bg-muted flex size-8 items-center justify-center rounded-full">
              <Users className="text-muted-foreground size-4" />
            </div>
          )}
          <div>
            <div className="text-foreground text-sm font-medium">{seat.assigneeLabel}</div>
            <div className="text-muted-foreground text-xs">
              {seat.status === "pending_accept" ? (
                <Trans>Pending acceptance</Trans>
              ) : (
                <Trans>Active</Trans>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-[10px]">
            {seat.allocationType}
          </Badge>
          <Button
            size="sm"
            variant="ghost"
            disabled={isRevoking}
            onClick={onRevokeSeat}
            className="size-11 p-0 sm:size-8"
            title={revokeTitle}
            aria-label={revokeTitle}
          >
            {isRevoking ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Trash2 className="size-3.5" />
            )}
          </Button>
        </div>
      </div>

      {/* Packs attached to this seat. Move / return to inventory are deferred
          to the next renewal; stop-renewal keeps the current period. */}
      {packs.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {packs.map((p) => (
            <PackAssignedRow
              key={p.id}
              pack={p}
              label={labelPack(p)}
              canMove={canMovePacks}
              busy={busyPackId === p.id}
              onMove={() => onMovePack(p.id)}
              onDetach={() => onDetachPack(p.id)}
              onCancelSchedule={() => onCancelSchedule(p.id)}
              onStopRenewal={() => onStopRenewal(p.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PackAssignedRow({
  pack,
  label,
  canMove,
  busy,
  onMove,
  onDetach,
  onCancelSchedule,
  onStopRenewal,
}: {
  pack: Pack;
  label: string;
  canMove: boolean;
  busy: boolean;
  onMove: () => void;
  onDetach: () => void;
  onCancelSchedule: () => void;
  onStopRenewal: () => void;
}) {
  const { t } = useLingui();
  const hasSchedule = pack.scheduledAt != null;
  const moveLabel = t`Move to another seat`;
  const detachLabel = t`Return to inventory`;
  const stopRenewalLabel = t`Stop renewal`;

  return (
    <div
      className={cn(
        "bg-muted/40 flex flex-wrap items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-xs",
        hasSchedule && "bg-warning-soft",
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        <Package className="text-muted-foreground size-3.5" />
        <span className="truncate font-medium">
          +{pack.units} {label}
        </span>
        {hasSchedule && (
          <Badge variant="warning" className="gap-1 text-[10px]">
            <Clock className="size-2.5" />
            {pack.scheduledTargetOwnerId === null ? (
              <Trans>Move to inventory</Trans>
            ) : (
              <Trans>Reassign pending</Trans>
            )}
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-1">
        {busy && <Loader2 className="text-muted-foreground size-3.5 animate-spin" />}
        {hasSchedule ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={onCancelSchedule}
            className="h-11 px-3 text-xs sm:h-7 sm:px-2"
          >
            <Trans>Cancel change</Trans>
          </Button>
        ) : (
          <>
            {canMove && (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={onMove}
                className="size-11 p-0 sm:size-7"
                title={moveLabel}
                aria-label={moveLabel}
              >
                <ArrowLeftRight className="size-3.5" />
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={onDetach}
              className="size-11 p-0 sm:size-7"
              title={detachLabel}
              aria-label={detachLabel}
            >
              <Minus className="size-3.5" />
            </Button>
          </>
        )}
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={onStopRenewal}
          className="size-11 p-0 sm:size-7"
          title={stopRenewalLabel}
          aria-label={stopRenewalLabel}
        >
          <Trash2 className="size-3" />
        </Button>
      </div>
    </div>
  );
}
